// signin-draft.ts — preserve the home composer's in-progress draft across a
// sign-in detour (flow F02, platform.signin.preserves_prompt).
//
// Root cause: CreatorRoot renders the sign-in / account / any non-home route by
// UNMOUNTING <App key="root"> (CreatorRoot.tsx ~159/198). Unmounting App tears
// down HomeV2 and with it the composer's LOCAL state — the typed prompt, the
// quick/deep mode, and the attached files. So a creator who types an idea, taps
// "Sign in", and comes back lands on an empty box; their draft is gone.
//
// The fix: stash a lightweight snapshot of the draft in sessionStorage while the
// user is composing, and rehydrate it when the composer next mounts. The stash
// protects against navigating to ANY non-home route (account → sign-in is the
// real F02 click-path), not just #signin.
//
// Honesty note: File objects CANNOT be serialized to sessionStorage and cannot
// survive a reload. We persist only the file NAMES and, on return, honestly ask
// the user to re-attach them — never silently pretend the files are still there.

import type { GenerationMode } from '../components/PromptInput';

const KEY = 'vs:creator:composer-draft';

/** Cap on remembered attachment names — a sane bound on stash size. */
const MAX_NAMES = 12;

export interface ComposerDraft {
  /** The raw prompt text the user had typed but not yet sent. */
  prompt: string;
  /** The selected build mode (quick | deep). */
  mode: GenerationMode;
  /**
   * Names of attached files. The File blobs themselves cannot be serialized or
   * survive the sign-in round trip, so we keep the names ONLY and ask the user
   * to re-attach them on return.
   */
  assetNames: string[];
}

function isMode(v: unknown): v is GenerationMode {
  return v === 'quick' || v === 'deep';
}

/** True iff the draft holds something actually worth restoring. */
export function draftHasContent(d: { prompt?: string; assetNames?: string[] }): boolean {
  return Boolean(d.prompt && d.prompt.trim()) || Boolean(d.assetNames && d.assetNames.length);
}

/**
 * Persist the composer draft. A truly-empty draft CLEARS the stash rather than
 * writing a blank one, so a composer the user emptied never re-seeds itself on
 * a later mount.
 */
export function stashComposerDraft(draft: ComposerDraft): void {
  try {
    if (!draftHasContent(draft)) {
      clearComposerDraft();
      return;
    }
    const safe: ComposerDraft = {
      prompt: typeof draft.prompt === 'string' ? draft.prompt : '',
      mode: isMode(draft.mode) ? draft.mode : 'quick',
      // Names only — we deliberately never attempt to serialize File objects.
      assetNames: Array.isArray(draft.assetNames)
        ? draft.assetNames.filter((n): n is string => typeof n === 'string').slice(0, MAX_NAMES)
        : []
    };
    sessionStorage.setItem(KEY, JSON.stringify(safe));
  } catch {
    /* private mode / storage disabled — draft preservation is best-effort */
  }
}

/** Read the stashed draft, or null when absent / empty / unreadable. */
export function readComposerDraft(): ComposerDraft | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ComposerDraft>;
    const draft: ComposerDraft = {
      prompt: typeof parsed.prompt === 'string' ? parsed.prompt : '',
      mode: isMode(parsed.mode) ? parsed.mode : 'quick',
      assetNames: Array.isArray(parsed.assetNames)
        ? parsed.assetNames.filter((n): n is string => typeof n === 'string')
        : []
    };
    return draftHasContent(draft) ? draft : null;
  } catch {
    return null;
  }
}

/**
 * Drop the stash. Called once the draft has been consumed (rehydrated into a
 * live composer) or the user has successfully sent it (it is no longer an
 * unsent draft).
 */
export function clearComposerDraft(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* best-effort */
  }
}
